import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, WHATSAPP_LINK } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const SLIDES = [
  { src: IMAGES.hero, alt: "Familia mexicana elegante y feliz conviviendo en casa" },
  { src: IMAGES.heroAuto, alt: "Familia feliz viajando en su coche por carretera al atardecer" },
  { src: IMAGES.heroConvivio, alt: "Familia conviviendo y riendo en una comida al aire libre" },
  { src: IMAGES.heroEmpresario, alt: "Empresario sonriendo, pleno y tranquilo en su oficina" },
];

export const Hero = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);

  useEffect(() => {
    SLIDES.slice(1).forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
    const timer = setInterval(() => setActive((a) => (a + 1) % SLIDES.length), 5200);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        {SLIDES.map((slide, i) => (
          <img
            key={slide.src}
            src={slide.src}
            alt={i === active ? slide.alt : ""}
            aria-hidden={i !== active}
            loading={i === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1600ms] ease-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#06273B]/95 via-[#0C5F8A]/70 to-[#1BA8E0]/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#06273B]/75 via-transparent to-[#06273B]/40" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-36 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 backdrop-blur-md"
          data-testid="hero-badge"
        >
          <span className="h-2 w-2 rounded-full bg-[#F5B21B]" />
          <span className="font-display text-xs font-medium uppercase tracking-[0.22em] text-white/90">
            Desde 1966 · Tres generaciones
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="max-w-3xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              60 años protegiendo
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.48, ease: [0.22, 1, 0.36, 1] }}
            >
              lo que más{" "}
              <span className="relative inline-block">
                <span className="relative z-10">quieres.</span>
                <motion.span
                  aria-hidden="true"
                  className="absolute -bottom-0.5 left-0 z-0 h-[0.32em] w-full rounded-full bg-[#F5B21B]"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.7, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformOrigin: "left" }}
                />
              </span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg"
        >
          Seguros de auto, hogar, gastos médicos y vida con las mejores aseguradoras de México, y la
          asesoría cercana de una familia que lleva tres generaciones cuidándote.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            type="button"
            onClick={() => scrollToId("contacto")}
            data-testid="hero-cta-cotiza"
            className="rounded-full bg-[#F5B21B] px-9 py-4 text-center font-display text-base font-semibold text-[#0C5F8A] shadow-[0_14px_34px_-8px_rgba(245,178,27,0.65)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#ffc63a]"
          >
            Cotiza tu seguro
          </button>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-white/80 px-8 py-[14px] font-display text-base font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:text-[#0C5F8A]"
          >
            <WhatsAppIcon className="h-5 w-5" />
            Escríbenos por WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-12 flex items-center gap-2.5"
          data-testid="hero-slide-dots"
        >
          {SLIDES.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Ver foto ${i + 1}`}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === active ? "w-9 bg-[#F5B21B]" : "w-4 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </motion.div>
      </div>

      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex h-11 w-7 items-start justify-center rounded-full border-2 border-white/50 p-1.5"
        >
          <span className="h-2.5 w-1 rounded-full bg-[#F5B21B]" />
        </motion.div>
      </motion.div>
    </section>
  );
};
